import { Autocomplete, Box, Card, CardHeader, Dialog, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import { EtapaEnvioViewDTO, EtapaEnvioFields } from './types'
import { HttpEtapaEnvioMail } from './api'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import EditGruposEtapa from './EditGruposEtapa'
import CachedIcon from '@mui/icons-material/Cached'
import { FilterAltOff, Search } from '@mui/icons-material'
import { WrapperTable } from '../components'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'

function TableEtapaEnvioMail() {
    const { paisLogos } = usePaisLogo()
    const [tiposSistema, setTiposSistema] = useState<{ id: number; descripcion: string }[]>([])
    const [selectedTipo, setSelectedTipo] = useState<{ id: number; descripcion: string } | null>(null)
    const [selectedPais, setSelectedPais] = useState<{ id: number; descripcion: string } | null>(null)
    const [rows, setRows] = useState<EtapaEnvioViewDTO[]>([])
    const [loading, setLoading] = useState(false)
    const [filter, setFilter] = useState('')
    const [selectedEtapa, setSelectedEtapa] = useState<EtapaEnvioViewDTO | null>(null)

    useEffect(() => {
        HttpCacheWorkflow.getSistemasTipos().then(data =>
            setTiposSistema(data.map(s => ({ id: s.id, descripcion: s.descripcion })))
        )
    }, [])

    const fetchData = () => {
        if (!selectedTipo) { setRows([]); 

return }
        setLoading(true)
        HttpEtapaEnvioMail.getByTipoSistema(selectedTipo.id, selectedPais?.id)
            .then(setRows)
            .finally(() => setLoading(false))
    }

    useEffect(() => { fetchData() }, [selectedTipo, selectedPais])

    // Agrupar: una fila por etapa+sistema, concatenar grupos
    const groupedRows = (() => {
        const map = new Map<string, { etapa: EtapaEnvioViewDTO; grupos: string[] }>()
        rows.forEach(r => {
            const key = `${r[EtapaEnvioFields.CodSistema]}-${r[EtapaEnvioFields.IdEtapa]}`
            if (!map.has(key)) {
                map.set(key, { etapa: r, grupos: [] })
            }
            if (r[EtapaEnvioFields.NombreGrupo]) {
                map.get(key)!.grupos.push(r[EtapaEnvioFields.NombreGrupo]!)
            }
        })
        
return Array.from(map.values()).map(({ etapa, grupos }) => ({
            ...etapa,
            gruposConcat: grupos.join(', ') || '-'
        }))
    })()

    const filteredRows = filter
        ? groupedRows.filter(r =>
            r[EtapaEnvioFields.NombreMostrar]?.toLowerCase().includes(filter.toLowerCase()) ||
            r[EtapaEnvioFields.NombreSistemaPrincipal]?.toLowerCase().includes(filter.toLowerCase()) ||
            r.gruposConcat?.toLowerCase().includes(filter.toLowerCase())
        )
        : groupedRows

    const columnas: GridColDef[] = [
        { headerName: 'País', field: EtapaEnvioFields.NombreSistemaPrincipal, flex: 1 },
        { headerName: 'Sistema - Etapa', field: EtapaEnvioFields.NombreMostrar, flex: 3 },
        { headerName: 'Grupos de envío', field: 'gruposConcat', flex: 3 },
        {
            field: 'actions', type: 'actions', width: 60,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    icon={<Tooltip title='Configurar grupos'><EditIcon color='info' /></Tooltip>}
                    label='Configurar' onClick={() => setSelectedEtapa(row)}
                />
            ]
        }
    ]

    return (
        <WrapperTable>
            <Card>
                <CardHeader
                    title='Configuración de Envío de Mail Extra por Etapa'
                    action={
                        selectedTipo ? <IconButton disabled={loading} onClick={fetchData}><CachedIcon /></IconButton> : null
                    }
                />

                {/* Filtros */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <Autocomplete
                            options={tiposSistema}
                            value={selectedTipo}
                            onChange={(_, v) => setSelectedTipo(v)}
                            getOptionLabel={(o) => o.descripcion}
                            isOptionEqualToValue={(o, v) => o.id === v?.id}
                            size='small'
                            renderInput={params => <TextField {...params} label='Tipo de Sistema *' />}
                        />
                    </Box>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <Autocomplete
                            options={paisLogos.map(s => ({ id: s.id, descripcion: s[EntityWithIdAndDescriptionFields.Description] }))}
                            value={selectedPais}
                            onChange={(_, v) => setSelectedPais(v)}
                            getOptionLabel={(o: any) => o.descripcion ?? ''}
                            isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                            size='small'
                            renderInput={params => <TextField {...params} label='País' />}
                        />
                    </Box>
                    <TextField
                        value={filter} onChange={e => setFilter(e.target.value)}
                        label='Buscar' size='small' sx={{ minWidth: 200, flex: 1 }}
                        InputProps={{ startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                    />
                    <Tooltip title='Limpiar filtros'>
                        <IconButton size='small' onClick={() => { setFilter(''); setSelectedPais(null) }}>
                            <FilterAltOff fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ mx: 2, mb: 2 }}>
                    <DataGrid
                        disableColumnFilter disableColumnMenu
                        columns={columnas}
                        rows={filteredRows}
                        loading={loading}
                        getRowId={(row) => `${row[EtapaEnvioFields.CodSistema]}-${row[EtapaEnvioFields.IdEtapa]}`}
                        density='compact'
                        pagination
                        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                        pageSizeOptions={[25, 50, 75, 100]}
                    />
                </Box>
            </Card>

            <Dialog open={!!selectedEtapa} fullWidth maxWidth='md' keepMounted={false}>
                {selectedEtapa && (
                    <EditGruposEtapa
                        etapa={selectedEtapa}
                        allRows={rows}
                        onClose={() => { setSelectedEtapa(null); fetchData() }}
                    />
                )}
            </Dialog>
        </WrapperTable>
    )
}

export default TableEtapaEnvioMail
